import {
    ButtonType,
    DialogFooterButtonsWidget,
    DialogPositioningBehavior,
    DialogType,
    DialogWidget,
    DropdownButtonType,
    HostedStackedElementContainer, List,
    SingleSelectDropdown,
} from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '~src/codegen/Localize';
import { DataSources } from '~src/datasource/resources/Datasources';
import { cssClass, Style } from '~src/style';

import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { L10n } from '../../ts/L10n/L10n';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { IRemoteQueryAgentOption } from '../services/ChangeDatasourceRemoteQueryAgentAction';

export interface ChangeDatasourceRemoteQueryAgentDialogProps {
    prompt: string;
    remoteQueryAgentOptions: IRemoteQueryAgentOption[];
    initialSelectedAgent: string;
    datasource: IDataSourceDetailInfo;
    dialogWidth: number;
    close: () => void;
    confirm: (datasourceId: string, selectedAgentName: string) => void;
}

export interface ChangeDatasourceRemoteQueryAgentDialogState {
    selectedAgentName: string;
}

export class ChangeDatasourceRemoteQueryAgentDialog extends React.Component<ChangeDatasourceRemoteQueryAgentDialogProps, ChangeDatasourceRemoteQueryAgentDialogState>{

    constructor(props: ChangeDatasourceRemoteQueryAgentDialogProps) {
        super(props);
        this.state = {
            selectedAgentName: props.initialSelectedAgent
        };
    }

    public static async open(datasource: IDataSourceDetailInfo,
        remoteQueryAgentOptions: IRemoteQueryAgentOption[],
        initialSelectedAgent: string,
        defaultOrDisabledAgentFunc: (selectedAgentName: string) => boolean): Promise<IResult> {

        return new Promise((resolve, reject) => {

            const hostContainer = new HostedStackedElementContainer();
            const close = () => { hostContainer && hostContainer.remove(); };
            const props: ChangeDatasourceRemoteQueryAgentDialogProps = {
                prompt: Messages.editDatasourceRemoteQueryAgentDialogPrompt({ DATASOURCE_NAME: L10n.elideIfLong(datasource.name) }),
                remoteQueryAgentOptions,
                initialSelectedAgent,
                datasource,
                dialogWidth: 400,
                close,
                confirm: (datasourceId: string, selectedAgentName: string) => {
                    close();
                    const useDefaultOrDisabledAgent = defaultOrDisabledAgentFunc(selectedAgentName);
                    const remoteAgentName = useDefaultOrDisabledAgent ? null : selectedAgentName;

                    DataSources.setRemoteQueryAgent(datasourceId, remoteAgentName, useDefaultOrDisabledAgent)
                        .then((result: IResult) => {
                            if (result.errors && result.errors.length) {
                                ToasterService.instance.pop({
                                    type: ToastType.Error,
                                    text: Messages.errorUnknown()
                                });
                            } else {
                                ToasterService.instance.pop({
                                    type: ToastType.Info,
                                    text: Messages.editDatasourceRemoteQueryAgentSuccess()
                                });
                            }
                            resolve(result);
                        })
                        .catch(error => reject(error));
                }
            };

            const dialog = React.createElement(ChangeDatasourceRemoteQueryAgentDialog, props);
            hostContainer.render(dialog);
        });
    }

    public renderFooter() {
        return <DialogFooterButtonsWidget
            buttons={[
                {
                    handleClick: () => this.props.close(),
                    buttonType: ButtonType.Outline,
                    children: Messages.cancel(),
                    testId: 'footer-cancel'
                },
                {
                    handleClick: () => this.props.confirm(
                        this.props.datasource.id,
                        this.state.selectedAgentName
                    ),
                    buttonType: ButtonType.Primary,
                    children: Messages.save(),
                    testId: 'footer-confirm',
                },
            ]}
            testId="change-remote-agent-dialog-footer"
        />;
    }

    public render(): React.ReactNode {
        const baseHeaderStyle: Style = {
            fontWeight: 'normal',
            fontSize: 20,
            marginTop: '0px',
            marginBottom: '18px'
        };
        const dialogHeaderStyle = cssClass('refresh-dialog', 'dialog-header', baseHeaderStyle);

        return <DialogWidget
            testId={'change-remote-agent-dialog'}
            type={DialogType.NormalWithoutTitleBar}
            title={Messages.editDatasourceRemoteQueryAgentDialogTitle()}
            onCloseRequest={this.props.close}
            showCloseButton={true}
            positioningBehavior={DialogPositioningBehavior.Centered}
            isModal={true}
            footerContent={this.renderFooter()}
            handleGlassClick={this.props.close}
            isGlassTransparent={false}
            dialogWidth={this.props.dialogWidth}
            dialogStyle={{ paddingLeft: 16, paddingRight: 16 }}
        >
            <div>
                <h1 className={dialogHeaderStyle}>{Messages.editDatasourceRemoteQueryAgentDialogTitle()}</h1>
                <span>{this.props.prompt}</span>
                <div style={{ marginTop: 32, marginBottom: 4, paddingLeft: 20 }}>
                    <SingleSelectDropdown
                        testId={'remote-agent-dropdown'}
                        type={DropdownButtonType.Outline}
                        list={<List items={this.props.remoteQueryAgentOptions.map((agent: IRemoteQueryAgentOption) => ({
                            content: agent.display,
                            value: agent.remoteAgentName,
                            testId: `change-remote-agent-${agent.remoteAgentName}`
                        }))} />}
                        value={this.state.selectedAgentName}
                        onChange={(agentName: string) => this.setState({ selectedAgentName: agentName })}
                        style={{ width: 300 }}
                        label={''}
                    />
                </div>
            </div>
        </DialogWidget>;
    }
}
